import React, { useContext, useEffect } from "react";
import styled from "styled-components";

import { FlexBox, HSpace, NumericTextInput } from "@examspark/common-ui";
import { getShadedCount, scoreFractionModel } from "@examspark/score";
import { Stem, StemFractionModelConstruct } from "@examspark/types";

import Mark from "../../common/Mark";
import QuestionContext from "../../QuestionContext";
import PieChart from "../common/graph/pie-chart/PieChart";
import Prompt from "../shared/Prompt";

import ItemFractionModelCell from "./FractionModelCell";

export const MAX_NUMBER_OF_SHADEABLES = 10000;

export interface ItemFractionModelProps {
  data: StemFractionModelConstruct;
}

const Row = styled.div`
  display: flex;
  border-top: solid 1px #333;

  &:first-child {
    border-top: none;
  }
`;

const ModelContainer = styled.div`
  border: solid 1px #ddd;
  padding: 1rem;
  background-color: #fff;
  display: flex;
  justify-content: center;
`;

const Field = styled(HSpace)`
  align-items: center;
`;
const FieldLabel = styled.div`
  flex: 1;
`;
const FieldInput = styled.div`
  width: 10rem;

  input {
    width: 100%;
  }
`;

interface MarkProps {
  mark?: "correct" | "incorrect";
}

const Styled = styled.div<MarkProps>`
  display: flex;
  position: relative;
  padding: 2rem 1.5rem;
  border-top: solid 3px transparent;
  max-width: 50rem;

  ${(props) => {
    if (props.mark === "correct") {
      return `
      background-color: #e4f3e4;
      border-top: solid 3px #9cb39c;
      `;
    }
    if (props.mark === "incorrect") {
      return `
      background-color: #f3e2e2;
      border-top: solid 3px #b32e2b;
      `;
    }
  }}

  .markup {
    position: absolute;
    top: -11px;
    right: -8px;
  }
`;

const Matrix = styled.div`
  border: solid 1.25px #333;
  background-color: #fff;
  margin: 0 1rem;
  width: fit-content;
`;

const initArray = (length: number) => Array.from(Array(length).keys());

const FractionModelConstruct: React.FC<
  React.PropsWithChildren<ItemFractionModelProps>
> = (props) => {
  const [player, dispatchPlayer] = useContext(QuestionContext);
  const partsInputRef = React.useRef<HTMLInputElement>(null);
  const shadedInputRef = React.useRef<HTMLInputElement>(null);

  const response:
    | {
        shadedWithGroups?: Stem.Response["shadedWithGroups"];
      }
    | undefined = player.response?.[props.data.id];

  // we build a 3d array purely to keep the types similar with the other
  // fraction model interactions
  const buildMatrix = (denominator: number, numerator: number = 0) => {
    const safeDenominator = Math.max(1, denominator);
    const groups = Math.max(1, Math.ceil(numerator / safeDenominator));

    if (props.data.config.model?.shape === "pie") {
      return initArray(groups).map(() =>
        initArray(1).map(() => initArray(denominator).map(() => 0)),
      );
    }

    if (props.data.config.model?.shape === "bar") {
      return initArray(groups).map(() =>
        initArray(denominator).map(() => initArray(1).map(() => 0)),
      );
    }

    return [];
  };

  const _numerator = getShadedCount(response?.shadedWithGroups);
  const _denominator =
    props.data.config.model?.shape === "bar"
      ? (response?.shadedWithGroups?.[0]?.length ?? 0)
      : (response?.shadedWithGroups?.[0]?.[0]?.length ?? 0);

  const setResponse = ({
    numerator: newNumerator,
    denominator: newDenominator,
  }: {
    numerator?: number;
    denominator?: number;
  }) => {
    // wait until the player is ready to receive responses
    if (!player.response) return;

    const denominator = newDenominator ?? _denominator ?? 0;
    const maxNumerator = denominator * 2;
    const rawNumerator = newNumerator ?? _numerator ?? 0;
    const numerator = Math.min(rawNumerator, maxNumerator);

    const answer = buildMatrix(denominator, numerator);

    let cellsLeftToShade = numerator;
    answer.forEach((group, groupIdx) => {
      group.forEach((row, rowIdx) => {
        row.forEach((col, colIdx) => {
          answer[groupIdx][rowIdx][colIdx] = cellsLeftToShade-- > 0 ? 1 : 0;
        });
      });
    });

    dispatchPlayer({
      type: "RESPONSE",
      data: {
        id: props.data.id,
        response: {
          shadedWithGroups: answer,
        },
      },
    });
  };

  // Score responses
  let mark: "incorrect" | "correct" | undefined;
  if (player.scoreResponses && response) {
    const score = scoreFractionModel(props.data, response);

    mark = score.max === score.actual ? "correct" : "incorrect";
  }

  // Reveal Key
  useEffect(() => {
    if (player.revealKey) {
      setResponse({
        numerator: +(props.data.scoring?.shaded?.[0].value ?? 0),
        denominator: +(props.data.config.model?.columns ?? 0),
      });
    }
  }, [player.revealKey]);

  return (
    <>
      <Prompt data={props.data.content.prompt} />
      <Styled mark={mark}>
        {mark && <Mark type={mark} />}
        <FlexBox>
          <Field>
            <FieldLabel>Enter the total number of parts</FieldLabel>
            <FieldInput>
              <NumericTextInput
                data-cy="total_parts"
                ref={partsInputRef}
                value={_denominator}
                type="number"
                min={0}
                max={99}
                onChange={(e) => {
                  if (e.target.value === "") {
                    setResponse({
                      denominator: 0,
                    });
                    return;
                  }

                  setResponse({
                    denominator: Number(e.target.value),
                  });
                }}
                onClick={() => partsInputRef.current?.select()}
              />
            </FieldInput>
          </Field>
          <Field>
            <FieldLabel>Enter the number of shaded parts</FieldLabel>
            <FieldInput>
              <NumericTextInput
                data-cy="shaded_parts"
                type="number"
                ref={shadedInputRef}
                value={_numerator}
                maxLength={2}
                max={_denominator * 2}
                min={0}
                onChange={(e) => {
                  if (e.target.value === "") {
                    setResponse({ numerator: 0 });
                    return;
                  }

                  const value = Number(e.target.value);

                  const clamped = Math.min(value, _denominator * 2);

                  setResponse({ numerator: clamped });
                }}
                onClick={() => shadedInputRef.current?.select()}
              />
            </FieldInput>
          </Field>
          {!!Number(_denominator) && (
            <ModelContainer>
              {props.data.config.model?.shape === "bar" && (
                <>
                  {response?.shadedWithGroups?.map((groups, groupIdx) => (
                    <Matrix key={`group-${groupIdx}`} data-cy="group">
                      {groups.map((rows, rowIdx) => (
                        <Row key={`row-${groupIdx}`} data-cy="row">
                          {rows.map((cols, colIdx) => (
                            <ItemFractionModelCell
                              key={`cell-${groupIdx}-${rowIdx}-${colIdx}`}
                              data-cy="cell"
                              selected={
                                !!response.shadedWithGroups?.[groupIdx]?.[
                                  rowIdx
                                ]?.[colIdx]
                              }
                            />
                          ))}
                        </Row>
                      ))}
                    </Matrix>
                  ))}
                </>
              )}

              {props.data.config.model?.shape === "pie" &&
                response?.shadedWithGroups && (
                  <>
                    {response.shadedWithGroups?.map((group, groupIdx) => (
                      <Matrix
                        key={`group-${groupIdx}`}
                        data-cy="group"
                        style={{ borderColor: "transparent" }}
                      >
                        <PieChart data={group[0]} />
                      </Matrix>
                    ))}
                  </>
                )}
            </ModelContainer>
          )}
        </FlexBox>
      </Styled>
    </>
  );
};

export default FractionModelConstruct;
